import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../AppIcon';
import { PROFILE_CONSTANTS } from '../../pages/va-profile-settings/constants/profileConstants';
import { serializeProfileData } from '../../pages/va-profile-settings/utils/dataProcessing';

const { SKILL_CATEGORIES, INDUSTRY_SPECIALIZATIONS, PROJECT_TYPES, AVAILABILITY_STATUSES, EXPERIENCE_CATEGORIES } = PROFILE_CONSTANTS;

const LANGUAGE_OPTIONS = [
  'English', 'Spanish', 'French', 'German', 'Portuguese',
  'Mandarin', 'Japanese', 'Arabic', 'Hindi', 'Russian', 'Other',
];

const STEPS = [
  { id: 1, title: 'Personal Info',          icon: 'User',        description: 'Basic contact details' },
  { id: 2, title: 'Professional Summary',   icon: 'Briefcase',   description: 'Your headline & background' },
  { id: 3, title: 'Skills',                 icon: 'Award',       description: 'What you excel at' },
  { id: 4, title: 'Work Experience',        icon: 'BarChart2',   description: 'Your career history' },
  { id: 5, title: 'Availability',   icon: 'DollarSign',  description: 'When and how much' },
  { id: 6, title: 'Preferences',            icon: 'Sliders',     description: 'Industries, languages & style' },
  { id: 7, title: 'Portfolio',      icon: 'Link',        description: 'Where to find your work' },
  { id: 8, title: 'Review & Complete',      icon: 'CheckCircle', description: 'Confirm and save' },
];

const inputClass = 'w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition text-sm';
const labelClass = 'block text-sm font-medium text-text-primary mb-1.5';

const CheckboxGrid = ({ options, selected, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
    {options.map((option) => {
      const isChecked = selected.includes(option);
      return (
        <div
          key={option}
          role="checkbox"
          aria-checked={isChecked}
          tabIndex={0}
          onClick={() => onChange(option)}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(option); } }}
          className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
            isChecked
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-border hover:border-primary/40 hover:bg-surface-alt'
          }`}
        >
          <span className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
            isChecked ? 'bg-primary border-primary' : 'border-border'
          }`}>
            {isChecked && (
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="text-sm font-medium">{option}</span>
        </div>
      );
    })}
  </div>
);

export default function ProfileWizard({ onSuccess }) {
  const { userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    location: '',
    timezone: '',
    phone: '',
    title: '',
    bio: '',
    experience_level: '',
    experience_years: '',
    skills: [],
    work_experience: [],
    availability_status: 'available',
    availability_hours: '',
    hourly_rate: '',
    minimum_rate: '',
    maximum_rate: '',
    preferred_project_types: [],
    industry_specialization: [],
    languages: [],
    work_style_description: '',
    website: '',
    linkedin: '',
    github_url: '',
    portfolio_url: '',
  });

  // Pre-fill form with existing profile data
  useEffect(() => {
    if (userProfile) {
      setFormData({
        full_name: userProfile.full_name || '',
        location: userProfile.location || '',
        timezone: userProfile.timezone || '',
        phone: userProfile.phone || '',
        title: userProfile.title || '',
        bio: userProfile.bio || '',
        experience_level: userProfile.experience_level || '',
        experience_years: userProfile.experience_years?.toString() || '',
        skills: Array.isArray(userProfile.skills)
          ? userProfile.skills.map(s => (typeof s === 'string' ? s : s?.name)).filter(Boolean)
          : [],
        work_experience: Array.isArray(userProfile.work_experience) ? userProfile.work_experience : [],
        availability_status: userProfile.availability_status || 'available',
        availability_hours: userProfile.availability_hours?.toString() || '',
        hourly_rate: userProfile.hourly_rate?.toString() || '',
        minimum_rate: userProfile.minimum_rate?.toString() || '',
        maximum_rate: userProfile.maximum_rate?.toString() || '',
        preferred_project_types: Array.isArray(userProfile.preferred_project_types) ? userProfile.preferred_project_types : [],
        industry_specialization: Array.isArray(userProfile.industry_specialization) ? userProfile.industry_specialization : [],
        languages: Array.isArray(userProfile.languages) ? userProfile.languages : [],
        work_style_description: userProfile.work_style_description || '',
        website: userProfile.website || '',
        linkedin: userProfile.linkedin || '',
        github_url: userProfile.github_url || '',
        portfolio_url: userProfile.portfolio_url || '',
      });
    }
  }, [userProfile]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleSkill = (skill) => {
    const current = formData.skills;
    set('skills', current.includes(skill) ? current.filter(s => s !== skill) : [...current, skill]);
  };

  const toggleArray = (field, value) => {
    const current = formData[field];
    set(field, current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
  };

  // Work experience entry management
  const addWorkEntry = () => {
    set('work_experience', [
      ...formData.work_experience,
      { id: Date.now(), company: '', position: '', start_date: '', end_date: '', description: '', current: false },
    ]);
  };

  const updateWorkEntry = (index, field, value) => {
    const updated = formData.work_experience.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    );
    set('work_experience', updated);
  };

  const removeWorkEntry = (index) => {
    set('work_experience', formData.work_experience.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    if (currentStep === 1 && !formData.full_name.trim()) {
      setStepError('Full name is required to continue.');
      return false;
    }
    if (currentStep === 2 && !formData.title.trim()) {
      setStepError('Professional title is required to continue.');
      return false;
    }
    setStepError('');
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setCurrentStep(s => Math.min(s + 1, STEPS.length));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStepError('');
    setCurrentStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setStepError('');
    try {
      const payload = {
        ...formData,
        experience_years: formData.experience_years ? parseInt(formData.experience_years, 10) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        minimum_rate: formData.minimum_rate ? parseFloat(formData.minimum_rate) : null,
        maximum_rate: formData.maximum_rate ? parseFloat(formData.maximum_rate) : null,
        availability_hours: formData.availability_hours || null,
      };
      const serialized = serializeProfileData(payload);
      const result = await updateProfile(serialized);
      if (!result?.success) {
        setStepError(result?.error || 'Failed to save your profile. Please try again.');
        return;
      }
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/va-dashboard');
      }
    } catch (err) {
      console.error('ProfileWizard submit error:', err);
      setStepError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  // ─── Step Renderers ───────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
        <input type="text" value={formData.full_name} onChange={e => set('full_name', e.target.value)}
          placeholder="Your full name" className={inputClass} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Location</label>
          <input type="text" value={formData.location} onChange={e => set('location', e.target.value)}
            placeholder="City, Country" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Timezone</label>
          <input type="text" value={formData.timezone} onChange={e => set('timezone', e.target.value)}
            placeholder="e.g. UTC-5, EST" className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>Phone Number</label>
        <input type="tel" value={formData.phone} onChange={e => set('phone', e.target.value)}
          placeholder="+1 (555) 123-4567" className={inputClass} />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div>
        <label className={labelClass}>Professional Title <span className="text-red-500">*</span></label>
        <input type="text" value={formData.title} onChange={e => set('title', e.target.value)}
          placeholder="e.g. Executive Virtual Assistant" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bio <span className="text-xs text-text-tertiary">(max 1000 chars)</span></label>
        <textarea value={formData.bio} onChange={e => set('bio', e.target.value)}
          rows={5} maxLength={1000}
          placeholder="Describe your professional background, strengths, and what makes you unique..."
          className={inputClass + ' resize-none'} />
        <p className="text-xs text-text-tertiary mt-1 text-right">{formData.bio.length}/1000</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Experience Level</label>
          <select value={formData.experience_level} onChange={e => set('experience_level', e.target.value)} className={inputClass}>
            <option value="">Select level</option>
            {EXPERIENCE_CATEGORIES.map(l => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Years of Experience</label>
          <input type="number" min="0" max="50" value={formData.experience_years}
            onChange={e => set('experience_years', e.target.value)}
            placeholder="e.g. 3" className={inputClass} />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <p className="text-sm text-text-secondary">Select all skills that apply. You can always update these later.</p>
      {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
        <div key={category}>
          <h4 className="text-sm font-semibold text-text-primary mb-2.5">{category}</h4>
          <CheckboxGrid
            options={skills}
            selected={formData.skills}
            onChange={toggleSkill}
          />
        </div>
      ))}
      {formData.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <span className="text-xs font-medium text-text-secondary">{formData.skills.length} selected:</span>
          {formData.skills.map(skill => (
            <span key={skill} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {skill}
              <button onClick={() => toggleSkill(skill)} className="hover:text-primary-dark" aria-label={`Remove ${skill}`}>
                <Icon name="X" size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">Add your relevant work history.</p>
        <button
          onClick={addWorkEntry}
          className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
        >
          <Icon name="Plus" size={15} /> Add Entry
        </button>
      </div>
      {formData.work_experience.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <Icon name="Briefcase" size={32} className="mx-auto text-text-tertiary mb-2" />
          <p className="text-sm text-text-secondary">No work experience added yet.</p>
          <button onClick={addWorkEntry} className="mt-3 text-sm text-primary font-medium hover:underline">Add your first entry</button>
        </div>
      )}
      {formData.work_experience.map((entry, index) => (
        <div key={entry.id ?? index} className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-primary">Entry {index + 1}</span>
            <button onClick={() => removeWorkEntry(index)} className="text-red-400 hover:text-red-600 transition-colors" aria-label="Remove entry">
              <Icon name="Trash2" size={15} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Company</label>
              <input type="text" value={entry.company} onChange={e => updateWorkEntry(index, 'company', e.target.value)}
                placeholder="Company name" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Job Title</label>
              <input type="text" value={entry.position} onChange={e => updateWorkEntry(index, 'position', e.target.value)}
                placeholder="Your role" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="month" value={entry.start_date} onChange={e => updateWorkEntry(index, 'start_date', e.target.value)}
                className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="month" value={entry.end_date} onChange={e => updateWorkEntry(index, 'end_date', e.target.value)}
                disabled={entry.current} className={inputClass + (entry.current ? ' opacity-50' : '')} />
              <label className="flex items-center gap-1.5 mt-1.5 text-xs text-text-secondary cursor-pointer">
                <input type="checkbox" checked={entry.current}
                  onChange={e => updateWorkEntry(index, 'current', e.target.checked)}
                  className="rounded border-border" />
                Currently working here
              </label>
            </div>
          </div>
          <div>
            <label className={labelClass}>Description <span className="text-text-tertiary">(optional)</span></label>
            <textarea value={entry.description} onChange={e => updateWorkEntry(index, 'description', e.target.value)}
              rows={3} placeholder="Key responsibilities and achievements..."
              className={inputClass + ' resize-none'} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-5">
      <div>
        <label className={labelClass}>Availability Status</label>
        <select value={formData.availability_status} onChange={e => set('availability_status', e.target.value)} className={inputClass}>
          {AVAILABILITY_STATUSES.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass}>Hours Available Per Week</label>
        <input type="number" min="1" max="80" value={formData.availability_hours}
          onChange={e => set('availability_hours', e.target.value)}
          placeholder="e.g. 40" className={inputClass} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Hourly Rate (USD)</label>
          <input type="number" min="0" value={formData.hourly_rate}
            onChange={e => set('hourly_rate', e.target.value)}
            placeholder="e.g. 25" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Minimum Rate</label>
          <input type="number" min="0" value={formData.minimum_rate}
            onChange={e => set('minimum_rate', e.target.value)}
            placeholder="e.g. 20" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Maximum Rate</label>
          <input type="number" min="0" value={formData.maximum_rate}
            onChange={e => set('maximum_rate', e.target.value)}
            placeholder="e.g. 50" className={inputClass} />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div>
        <label className={labelClass + ' mb-2.5'}>Industries You Specialize In</label>
        <CheckboxGrid
          options={INDUSTRY_SPECIALIZATIONS}
          selected={formData.industry_specialization}
          onChange={(v) => toggleArray('industry_specialization', v)}
        />
      </div>
      <div>
        <label className={labelClass + ' mb-2.5'}>Preferred Project Types</label>
        <CheckboxGrid
          options={PROJECT_TYPES}
          selected={formData.preferred_project_types}
          onChange={(v) => toggleArray('preferred_project_types', v)}
        />
      </div>
      <div>
        <label className={labelClass + ' mb-2.5'}>Languages</label>
        <CheckboxGrid
          options={LANGUAGE_OPTIONS}
          selected={formData.languages}
          onChange={(v) => toggleArray('languages', v)}
        />
      </div>
      <div>
        <label className={labelClass}>Work Style Description <span className="text-text-tertiary">(optional)</span></label>
        <textarea value={formData.work_style_description} onChange={e => set('work_style_description', e.target.value)}
          rows={3} placeholder="Describe how you prefer to work (e.g. independent, async-first, results-driven)..."
          className={inputClass + ' resize-none'} />
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-5">
      <div>
        <label className={labelClass}>Personal Website</label>
        <input type="url" value={formData.website} onChange={e => set('website', e.target.value)}
          placeholder="https://yoursite.com" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>LinkedIn Profile</label>
        <input type="url" value={formData.linkedin} onChange={e => set('linkedin', e.target.value)}
          placeholder="https://linkedin.com/in/yourname" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>GitHub Profile</label>
        <input type="url" value={formData.github_url} onChange={e => set('github_url', e.target.value)}
          placeholder="https://github.com/yourname" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Portfolio URL</label>
        <input type="url" value={formData.portfolio_url} onChange={e => set('portfolio_url', e.target.value)}
          placeholder="https://portfolio.com" className={inputClass} />
      </div>
    </div>
  );

  const renderStep8 = () => {
    const skills = formData.skills.slice(0, 8);
    return (
      <div className="space-y-5">
        <p className="text-sm text-text-secondary">Review your information before saving. You can always edit everything later in Profile Settings.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Name', value: formData.full_name },
            { label: 'Title', value: formData.title },
            { label: 'Location', value: formData.location },
            { label: 'Experience', value: formData.experience_level ? `${formData.experience_level} ${formData.experience_years ? `(${formData.experience_years} yrs)` : ''}` : '—' },
            { label: 'Availability', value: formData.availability_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—' },
            { label: 'Hourly Rate', value: formData.hourly_rate ? `$${formData.hourly_rate}/hr` : '—' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-surface-alt rounded-lg p-3">
              <p className="text-xs text-text-tertiary font-medium mb-0.5">{label}</p>
              <p className="text-sm font-semibold text-text-primary truncate">{value || '—'}</p>
            </div>
          ))}
        </div>
        {skills.length > 0 && (
          <div>
            <p className="text-xs text-text-tertiary font-medium mb-2">Skills ({formData.skills.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {skills.map(skill => (
                <span key={skill} className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full">{skill}</span>
              ))}
              {formData.skills.length > 8 && (
                <span className="text-xs text-text-tertiary px-2 py-1">+{formData.skills.length - 8} more</span>
              )}
            </div>
          </div>
        )}
        {formData.work_experience.length > 0 && (
          <div>
            <p className="text-xs text-text-tertiary font-medium mb-2">Work Experience ({formData.work_experience.length} entries)</p>
            <div className="space-y-1.5">
              {formData.work_experience.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Icon name="Briefcase" size={13} className="text-text-tertiary flex-shrink-0" />
                  <span className="font-medium">{entry.position || 'Untitled'}</span>
                  {entry.company && <span className="text-text-secondary">at {entry.company}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      default: return null;
    }
  };

  const currentStepMeta = STEPS[currentStep - 1];

  return (
    <div className="mx-auto">
      <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">

        {/* ── Progress Header ── */}
        <div className="px-6 pt-6 pb-0">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Step {currentStep} of {STEPS.length}
            </span>
            <span className="text-xs text-text-tertiary">
              {Math.round(progressPercent)}% complete
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 bg-border rounded-full mb-5">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500 ease-in-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Step pills */}
          <div className="flex gap-1.5 overflow-x-auto pb-4 scrollbar-hide">
            {STEPS.map((step) => {
              const isDone = step.id < currentStep;
              const isActive = step.id === currentStep;
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-all ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : isDone
                        ? 'bg-green-100 text-green-700'
                        : 'bg-white/60 text-text-tertiary'
                  }`}
                >
                  {isDone ? (
                    <Icon name="Check" size={11} />
                  ) : (
                    <span className={`w-6 h-6 rounded-full text-sm flex items-center justify-center font-bold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-border text-text-tertiary'
                    }`}>
                      {step.id}
                    </span>
                  )}
                  <span className="hidden text-sm sm:inline">{step.title}</span>
                  <span className="sm:hidden">{step.id}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Step Body ── */}
        <div className="px-6 py-6">
          {/* Step header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={currentStepMeta.icon} size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary leading-tight">{currentStepMeta.title}</h2>
              <p className="text-sm text-text-secondary">{currentStepMeta.description}</p>
            </div>
          </div>

          {/* Error banner */}
          {stepError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
              <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
              <span>{stepError}</span>
            </div>
          )}

          {/* Dynamic step content */}
          <div key={currentStep} className="animate-in fade-in slide-in-from-right-4 duration-300">
            {renderCurrentStep()}
          </div>
        </div>

        {/* ── Navigation Footer ── */}
        <div className="px-6 py-4 bg-surface-alt border-t border-border flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-text-secondary border border-border hover:bg-surface hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <Icon name="ChevronLeft" size={16} />
            Back
          </button>

          <div className="flex items-center gap-2">
            {STEPS.map((step) => (
              <span
                key={step.id}
                className={`rounded-full transition-all duration-300 ${
                  step.id === currentStep
                    ? 'w-4 h-2 bg-primary'
                    : step.id < currentStep
                      ? 'w-2 h-2 bg-green-400'
                      : 'w-2 h-2 bg-border'
                }`}
              />
            ))}
          </div>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition shadow-sm"
            >
              Next
              <Icon name="ChevronRight" size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              {loading ? 'Saving...' : 'Complete Profile'}
              {!loading && <Icon name="CheckCircle" size={16} />}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

